import React from 'react';

import { SearchRef } from '@/components/Search';
import SearchPanel from '@/components/SearchPanel';

import { TipoItemComplementar } from '../../models';
import { useAllowedMenu } from '../../helpers/permissions';
import CondutaComplementarService, {
  ItemComplementarSearchResult,
} from '../../services/CondutaComplementarService';
import { useCondutaComplementarContext } from '../../providers/CondutaComplementarProvider';

import ArtefatoSearch from '../ArtefatoSearch';
import AddManipuladosButton from '../AddManipuladosButton';

import { Container } from './styles';

const ItemComplementarSearch: React.FC = () => {
  const options = useAllowedMenu();
  const {
    state: { generalType, isMenuOpen },
    setGeneralType,
    addItem,
  } = useCondutaComplementarContext();

  const [loading, setLoading] = React.useState(false);
  const [searchResults, setSearchResults] = React.useState<
    ItemComplementarSearchResult[] | undefined
  >();

  const searchRef = React.useRef<SearchRef>(null);

  const handleSearch = React.useCallback(
    async (type: TipoItemComplementar, term: string) => {
      if (term) {
        try {
          setLoading(true);
          const results = await CondutaComplementarService.search({
            term,
            type,
          });
          setSearchResults(results);
          // eslint-disable-next-line no-empty
        } catch {
        } finally {
          setLoading(false);
        }
      } else {
        setSearchResults(undefined);
      }
    },
    [],
  );

  const handleSelectSearchResult = React.useCallback(
    (result: ItemComplementarSearchResult) => {
      // Clear search
      setSearchResults(undefined);

      if (result.data) {
        // Items que são adicionados e exigem a confirmação antes de adicioná-los ao bundle
        const shouldConfirmFirst = [
          'encaminhamento',
          'prescricao-medica',
        ].includes(generalType);

        addItem({
          type: generalType,
          resource: result.data,
          itemState: {
            // Medicamentos e encaminhamentos já são adicionados em edição
            isEditing: shouldConfirmFirst,
            isAdding: shouldConfirmFirst,
          },
        });
      }

      if (searchRef.current) {
        searchRef.current.clear();
      }
    },
    [addItem, generalType],
  );

  const clearSearch = React.useCallback(() => {
    setSearchResults(undefined);

    if (searchRef.current) {
      searchRef.current.clear();
    }
  }, []);

  const handleTipoSelect = React.useCallback(
    (tipo: TipoItemComplementar) => {
      setGeneralType(tipo);
      clearSearch();
    },
    [clearSearch, setGeneralType],
  );

  const SearchResultsAction = React.useMemo(() => {
    if (generalType === 'manipulado') {
      return <AddManipuladosButton onAddManipulado={() => clearSearch()} />;
    }

    return <></>;
  }, [clearSearch, generalType]);

  React.useEffect(() => {
    // Clear search input and results states after toggle side menu
    clearSearch();

    if (isMenuOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [clearSearch, isMenuOpen]);

  return (
    <Container>
      <SearchPanel
        searching={loading}
        onSearchFocusOut={clearSearch}
        renderSearchInput={control => (
          <ArtefatoSearch
            tipoMenu={generalType}
            onTipoSelect={handleTipoSelect}
            onSearchArtefato={handleSearch}
            options={options}
            ref={searchRef}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...control}
          />
        )}
        results={searchResults}
        resultLabel={({ item: { label } }) => label}
        resultKeyExtractor={({ item: { value } }) => value}
        onResultSelect={handleSelectSearchResult}
        onResultsDismiss={clearSearch}
        ResultsFooter={SearchResultsAction}
      />
    </Container>
  );
};

export default ItemComplementarSearch;
